'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';

interface EmployerFormData {
  fullName: string;
  username: string;
  email: string;
  phone1: string;
  phone2: string;
  city: string;
  country: string;
  bio: string;
  password: string;
  confirmPassword: string;
}

interface EmployerRegisterFormProps {
  onSwitchToLogin: () => void;
}

const countries = [
  'Zimbabwe', 'South Africa', 'Botswana', 'Zambia', 'Mozambique',
  'Namibia', 'Malawi', 'Tanzania', 'Kenya', 'United Kingdom',
];

const zimbabweCities = [
  'Harare', 'Bulawayo', 'Mutare', 'Gweru', 'Kwekwe', 'Kadoma',
  'Masvingo', 'Chinhoyi', 'Bindura', 'Marondera',
];

const inputClass = "w-full px-4 py-2.5 text-sm bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all placeholder:text-slate-400 text-slate-800";
const inputStyle = { borderColor: 'rgba(232,93,4,0.20)' };
const selectClass = "w-full px-4 py-2.5 text-sm bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all appearance-none text-slate-800";

export default function EmployerRegisterForm({ onSwitchToLogin }: EmployerRegisterFormProps) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profilePic, setProfilePic] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EmployerFormData>();

  const password = watch('password');

  const handleProfilePicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setProfilePic(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: EmployerFormData) => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1600));
    toast.success('Account created! Redirecting to complete your profile...');
    router.push('/members-directory');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Social auth */}
      <div className="grid grid-cols-2 gap-3">
        <button type="button" className="flex items-center justify-center gap-2 py-2.5 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-all active:scale-[0.98] rounded-xl" style={{border: '1px solid rgba(232,93,4,0.18)'}}>
          <svg width="18" height="18" viewBox="0 0 24 24" className="flex-shrink-0">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Google
        </button>
        <button type="button" className="flex items-center justify-center gap-2 py-2.5 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-all active:scale-[0.98] rounded-xl" style={{border: '1px solid rgba(232,93,4,0.18)'}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2" className="flex-shrink-0">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          Facebook
        </button>
      </div>

      <div className="relative flex items-center gap-3">
        <div className="flex-1 h-px" style={{background: 'rgba(232,93,4,0.15)'}} />
        <span className="text-xs text-slate-400 font-medium">or register with email</span>
        <div className="flex-1 h-px" style={{background: 'rgba(232,93,4,0.15)'}} />
      </div>

      {/* Profile picture */}
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0" style={{background: 'rgba(232,93,4,0.06)', border: '2px dashed rgba(232,93,4,0.25)'}}>
          {profilePic ? (
            <img src={profilePic} alt="Profile preview" className="w-full h-full object-cover" />
          ) : (
            <Upload size={20} className="text-primary/50" />
          )}
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">
            Profile Picture <span className="text-slate-400 font-normal">(Optional)</span>
          </label>
          <label className="cursor-pointer inline-block px-3 py-1.5 bg-white text-xs font-medium text-slate-700 hover:bg-orange-50 transition-all rounded-lg" style={{border: '1px solid rgba(232,93,4,0.20)'}}>
            Upload Photo
            <input type="file" accept="image/*" className="hidden" onChange={handleProfilePicChange} />
          </label>
        </div>
      </div>

      {/* Full Name */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
        <input type="text" {...register('fullName', { required: 'Full name is required' })} placeholder="e.g. Grace Moyo" className={inputClass} style={inputStyle} />
        {errors.fullName && <p className="mt-1 text-xs text-red-600">{errors.fullName.message}</p>}
      </div>

      {/* Username */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Username <span className="text-red-500">*</span></label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">@</span>
          <input type="text" {...register('username', { required: 'Username is required', pattern: { value: /^[a-z0-9_]+$/, message: 'Lowercase letters, numbers, and underscores only' } })} placeholder="gracemoyo" className={`${inputClass} pl-8`} style={inputStyle} />
        </div>
        {errors.username && <p className="mt-1 text-xs text-red-600">{errors.username.message}</p>}
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email Address <span className="text-red-500">*</span></label>
        <input type="email" {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' } })} placeholder="grace@email.com" className={inputClass} style={inputStyle} />
        {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
      </div>

      {/* Phone numbers */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone 1 <span className="text-red-500">*</span></label>
          <input type="tel" {...register('phone1', { required: 'Primary phone is required' })} placeholder="+263 77 123 4567" className={inputClass} style={inputStyle} />
          {errors.phone1 && <p className="mt-1 text-xs text-red-600">{errors.phone1.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone 2 <span className="text-slate-400 font-normal">(Opt.)</span></label>
          <input type="tel" {...register('phone2')} placeholder="+263 71 987 6543" className={inputClass} style={inputStyle} />
        </div>
      </div>

      {/* Country + City */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Country <span className="text-red-500">*</span></label>
          <select {...register('country', { required: 'Country is required' })} className={selectClass} style={inputStyle}>
            <option value="">Select country</option>
            {countries.map((c) => <option key={`country-emp-${c}`} value={c}>{c}</option>)}
          </select>
          {errors.country && <p className="mt-1 text-xs text-red-600">{errors.country.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">City <span className="text-red-500">*</span></label>
          <select {...register('city', { required: 'City is required' })} className={selectClass} style={inputStyle}>
            <option value="">Select city</option>
            {zimbabweCities.map((c) => <option key={`city-emp-${c}`} value={c}>{c}</option>)}
          </select>
          {errors.city && <p className="mt-1 text-xs text-red-600">{errors.city.message}</p>}
        </div>
      </div>

      {/* Short Bio */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Short Bio <span className="text-red-500">*</span></label>
        <p className="text-xs text-slate-400 mb-1.5">Briefly describe yourself as an employer (max 200 characters)</p>
        <textarea {...register('bio', { required: 'A short bio is required', maxLength: { value: 200, message: 'Bio must be 200 characters or less' } })} rows={3} placeholder="e.g. Harare-based business owner looking for a reliable, experienced Home Manager..." className="w-full px-4 py-2.5 text-sm bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary transition-all resize-none placeholder:text-slate-400 text-slate-800" style={inputStyle} />
        {errors.bio && <p className="mt-1 text-xs text-red-600">{errors.bio.message}</p>}
      </div>

      {/* Password */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password <span className="text-red-500">*</span></label>
        <div className="relative">
          <input type={showPassword ? 'text' : 'password'} {...register('password', { required: 'Password is required', minLength: { value: 8, message: 'Password must be at least 8 characters' } })} placeholder="Min. 8 characters" className={`${inputClass} pr-11`} style={inputStyle} />
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
      </div>

      {/* Confirm Password */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Confirm Password <span className="text-red-500">*</span></label>
        <div className="relative">
          <input type={showConfirm ? 'text' : 'password'} {...register('confirmPassword', { required: 'Please confirm your password', validate: (v) => v === password || 'Passwords do not match' })} placeholder="Re-enter your password" className={`${inputClass} pr-11`} style={inputStyle} />
          <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.confirmPassword && <p className="mt-1 text-xs text-red-600">{errors.confirmPassword.message}</p>}
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 text-white text-sm font-bold rounded-xl hover:opacity-90 active:scale-[0.98] transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2"
        style={{ minHeight: '44px', background: 'linear-gradient(135deg, #F47B20 0%, #e8650a 100%)', boxShadow: '0 4px 14px rgba(232,93,4,0.30)' }}
      >
        {loading ? <><Loader2 size={16} className="animate-spin" /> Creating account...</> : 'Create Employer Account'}
      </button>

      <p className="text-center text-sm text-slate-500">
        Already registered?{' '}
        <button type="button" onClick={onSwitchToLogin} className="text-primary font-semibold hover:underline">Sign in</button>
      </p>
    </form>
  );
}